import React, { useCallback, useEffect } from 'react';
import { Box, Button, Paper, Alert, Snackbar } from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import PhotoUpload from '../Common/PhotoUpload';
import PersonalInfoFields from '../PersonalInfoFields';
import { useProfileForm } from '../../hooks/useProfileForm';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useImageUpload } from '../../hooks/useImageUpload';
import AdminApi from 'Service/Admin.api';
import { UserInitialStateType } from 'DataTypes/Redux';
import { useSelector } from 'react-redux';
import { SkillArr } from 'Constants/Arrays';
import { useDispatch } from 'react-redux';
import { setAlertData } from '../../../../Redux/Screen/Action';
import { setMyInfo } from '../../../../Redux/User/Action';
import { FormateMyInfoData } from 'Pages/Login/Formatter';

const ProfileForm: React.FC = () => {
  const {
    formData,
    errors,
    isDirty,
    isSubmitting,
    updateField,
    setErrors,
    setSubmitting,
    resetForm,
    updateFormData,
    replaceFormData,
  } = useProfileForm();

  const { validateField, validateForm, isFormValid } = useFormValidation();
  const { uploadImage, uploading: imageUploading } = useImageUpload();
  const dispatch = useDispatch<any>();
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const baseUrl = process.env.REACT_APP_BASE_URL || '';

  useEffect(() => {
    fetchPProfileData();
    updateFormData({
      profilePhoto: myInfo?.profileImageUrl || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myInfo?.id]);


  const fetchPProfileData = async () => {
    try {
      if (myInfo?.id != null) {
        const response = await AdminApi.getProfile(String(myInfo.id));

        const api = response?.data;
        
        if (api) {
          const fullName = [api.firstName, api.lastName].filter(Boolean).join(' ').trim();
          const isoDate = api.dateOfBirth ? new Date(api.dateOfBirth) : null;
          const yyyyMmDd = isoDate ? `${isoDate.getFullYear()}-${String(isoDate.getMonth() + 1).padStart(2, '0')}-${String(isoDate.getDate()).padStart(2, '0')}` : '';

          const organisationName = api?.corporateOrg?.sName || '';

          const skillsArray = Array.isArray(api.professionalSkills)
            ? api.professionalSkills
              .map((s: any) => String(s?.skillName || '').trim())
              .filter(Boolean)
            : [];
          const skillsString = skillsArray.join(', ');

          // Transform API skills to skillEntries format
          const skillEntries = Array.isArray(api.professionalSkills)
            ? api.professionalSkills.map((skill: any, index: number) => ({
              id: `skill_${Date.now()}_${index}`,
              skillName: skill.skillName || '',
              experienceYears: skill.numberOfExperienceYears || 0,
              panIndia: skill.servesPanIndia || false,
              selectedStates: skill.servesStates ? skill.servesStates.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
              description: skill.description || '',
            }))
            : [];

          replaceFormData({
            name: fullName || '',
            email: api.email || api.userName || '',
            phone: api.phoneNumber || '',
            address: api.address || '',
            city: api.city || '',
            state: api.state || '',
            postalCode: api.postalCode || '',
            dateOfBirth: yyyyMmDd,
            description: api.description || '',
            profilePhoto: api.profilePicturePath
              ? `${baseUrl}${api.profilePicturePath.startsWith('/') ? '' : '/'}${api.profilePicturePath}`
              : undefined,
            highestQualification: api.highestQualification || '',
            skills: skillsString,
            skillsChips: skillsArray,
            skillEntries: skillEntries,
            organisationName
          });
        }
      } else {
        throw new Error('User ID is missing');
      }
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
    }
  }

  const handleFieldChange = useCallback((name: string, value: string | any[]) => {
    updateField(name, value);
  }, [updateField]);

  const handleFieldBlur = useCallback((field: string) => {
    const error = validateField(field, formData[field as keyof typeof formData] as string);
    if (error) {
      setErrors({ ...errors, [field]: error });
    }
  }, [validateField, formData, errors, setErrors]);

  const handlePhotoUpload = async (file: File) => {
    try {
      console.log('Uploading photo:', file);
      const fd = new FormData();
      fd.append("file", file, file.name);
      Array.from(fd.entries()).forEach(([k, v]) => {
        console.log("FormData entry:", k, v);
      });
      const uploadedUrl = await AdminApi.UploadProfilePicture(file, String(myInfo.id));
      const rawUrl = uploadedUrl?.data?.profilePictureUrl || '';

      // 👇 Attach base URL only if not already a full URL
      const fullUrl = rawUrl.startsWith('http')
        ? rawUrl
        : `${baseUrl}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;

      // updateFormData({ profilePhoto: uploadedUrl.data.profilePictureUrl });
      updateFormData({ profilePhoto: fullUrl });

      dispatch(setMyInfo({
        ...myInfo,
        profileImageUrl: fullUrl,
      }));
      // updateFormData({ profilePhoto: uploadedUrl });
    } catch (error) {
      console.error('Photo upload failed:', error);
      throw error;
    }
  }


  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();

    // Step 1️⃣: Validate form
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    if (!isFormValid(validationErrors)) {
      dispatch(setAlertData(
        {
          alertMessage: 'Please fix the errors and try again.',
          alertVariant: 'error',
          openAlert: true,
        }
      ));
      return;
    }

    setSubmitting(true);

    try {
      // Step 2️⃣: Parse full name into firstName and lastName
      const nameParts = (formData.name || "").trim().split(" ");
      const FirstName = nameParts[0] || "";
      const LastName = nameParts.slice(1).join(" ") || "";

      // Step 3️⃣: Conditional payload building based on userType
      let payload;

      if (myInfo?.userType === "PROFESSIONAL") {
        // ✅ PROFESSIONAL payload
        const Skills = (formData.skillEntries || [])
          .filter(skill => skill.skillName)
          .map(skill => {
            const matchedSkill = SkillArr.find(
              s => s.label.toLowerCase() === skill.skillName.toLowerCase()
            );

            return {
              SkillId: matchedSkill ? matchedSkill.id : null,
              Rating: 0,
              NumberOfExperienceYears: skill.experienceYears || 0,
              Description: skill.description || "",
              ProfessionalFeesPerCallingRs: 0,
              ServesPanIndia: skill.panIndia || false,
              ...(skill.panIndia ? {} : { ServesStates: skill.selectedStates || [] }),
            };
          });

        payload = {
          FirstName,
          LastName,
          Email: formData.email,
          Address: formData.address,
          ContactNumber: formData.phone,
          City: formData.city,
          State: formData.state,
          PostalCode: formData.postalCode,
          DateOfBirth: formData.dateOfBirth,
          ProfilePicturePath: formData.profilePhoto || "",
          HighestQualification: formData.highestQualification || "",
          Description: formData.description || "",
          Skills,
        };
      } else if (myInfo?.userType === "USER" || myInfo?.userType === "CORPORATEUSER") {
        // ✅ USER payload (minimal)
        payload = {
          FirstName,
          LastName,
          Email: formData.email,
          ContactNumber: formData.phone,
          DateOfBirth: formData.dateOfBirth,
          Address: formData.address,
          State: formData.state,
          City: formData.city,
          PostalCode: formData.postalCode,
        };
      }


      // Step 5️⃣: API Call
      const response = await AdminApi.ProfileUpdate(
        String(myInfo?.id),
        payload,
        String(myInfo?.userType)
      );

      console.log("✅ Profile update response:", response);
      // setShowSuccessMessage(true);
      dispatch(setAlertData(
        {
          alertMessage: 'Profile Updated successfully!',
          alertVariant: 'success',
          openAlert: true,
        }
      ));
    } catch (error) {
      console.error("❌ Save failed:", error);
      // setShowErrorMessage(true);
      dispatch(setAlertData(
        {
          alertMessage: 'Failed to update profile. Please try again.',
          alertVariant: 'error',
          openAlert: true,
        }
      ));
    } finally {
      setSubmitting(false);
    }
  }, [validateForm, formData, setErrors, isFormValid, setSubmitting, myInfo?.id, myInfo?.userType]);


  const handleCancel = useCallback(() => {
    if (isDirty) {
      const confirmCancel = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (confirmCancel) {
        resetForm();
      }
    }
  }, [isDirty, resetForm]);


  return (
    <>
      <Box
        sx={{
          height: ['PROFESSIONAL', 'CORPORATEUSER'].includes(myInfo?.userType)
            ? '75vh'
            : '65vh',
          //         height: myInfo?.userType === 'PROFESSIONAL'
          // ? '75vh'
          // : myInfo?.userType === 'CORPORATEUSER'
          //   ? '75vh'
          //   : '65vh',
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Scrollable Form Content */}
        <Paper
          component="form"
          id="profile-form"
          onSubmit={handleSubmit}
          sx={{
            flex: 1,               // take remaining space
            overflowY: "auto",     // scroll inside
            // p: { xs: 2, sm: 3 }
          }}
        >
          <PhotoUpload
            currentPhoto={typeof formData.profilePhoto === 'string' ? formData.profilePhoto : undefined}
            onPhotoUpload={handlePhotoUpload}
            loading={imageUploading}
          />

          <PersonalInfoFields
            formData={formData}
            errors={errors}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
          />
        </Paper>

        {/* Sticky Footer with Actions */}
        <Box
          sx={{
            borderTop: "1px solid #dee2e6",
            p: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            backgroundColor: "#f8f9fa",
          }}
        >
          <Button
            type="submit"
            variant="contained"
            startIcon={<Save />}
            disabled={isSubmitting || !isDirty}
            sx={{
              backgroundColor: '#007bff',
              color: '#fff',
              '&:hover': { backgroundColor: '#0056b3' },
              '&:disabled': { backgroundColor: '#6c757d' }
            }}
            form="profile-form" // ensures it triggers form submit
          >
            {isSubmitting ? "Saving..." : "Save Profile"}
          </Button>

          <Button
            type="button"
            variant="outlined"
            startIcon={<Cancel />}
            onClick={handleCancel}
            disabled={isSubmitting}
            sx={{
              color: '#6c757d',
              borderColor: '#dee2e6',
              '&:hover': {
                backgroundColor: '#f8f9fa',
                borderColor: '#6c757d',
                color: '#495057',
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>



    </>
  );



};

export default ProfileForm;